import { useEffect, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ALLOWED_REACTIONS, QUICK_REACTIONS } from '../../shared/messages'
import type { Room } from '../types'
import type { SendFn } from '../App'

interface Props {
  itemText: string
  room: Room
  playerId: string
  send: SendFn
  /**
   * The element wrapped here becomes the long-press target. The wrapper
   * provides the picker popover and the reaction-count bubbles directly
   * below it.
   */
  children: ReactNode
}

const LONG_PRESS_MS = 450

// Approximate dimensions of the quick picker — used to clamp its anchor
// inside the viewport when computing fixed coordinates. The picker sizes
// itself to content, but these are stable enough for layout math.
const POPOVER_APPROX_WIDTH = 280
const POPOVER_APPROX_HEIGHT = 52
const VIEWPORT_MARGIN = 8

interface PopoverPos {
  top: number
  left: number
  below: boolean
}

export default function ItemReactions({ itemText, room, playerId, send, children }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState<PopoverPos | null>(null)
  const [fullPickerOpen, setFullPickerOpen] = useState(false)
  const [namesForEmoji, setNamesForEmoji] = useState<string | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressFired = useRef(false)
  const triggerRef = useRef<HTMLDivElement>(null)

  // Reactions for this item, grouped by emoji.
  const grouped = new Map<string, string[]>() // emoji → userIds
  for (const r of room.reactions) {
    if (r.itemText !== itemText) continue
    const list = grouped.get(r.emoji)
    if (list) list.push(r.userId)
    else grouped.set(r.emoji, [r.userId])
  }
  const myEmojis = new Set<string>()
  for (const [emoji, users] of grouped) {
    if (users.includes(playerId)) myEmojis.add(emoji)
  }

  const cancelTimer = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const onPointerDown = (e: ReactPointerEvent) => {
    // Ignore right-click / middle-click etc.
    if (e.button !== undefined && e.button !== 0) return
    longPressFired.current = false
    cancelTimer()
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true
      const rect = triggerRef.current?.getBoundingClientRect()
      if (rect) {
        // Anchor horizontally at the trigger's center, clamped so the
        // popover stays inside the viewport. Anchor vertically just above
        // the trigger; if there isn't room, flip to below.
        const vw = window.innerWidth
        const halfPop = POPOVER_APPROX_WIDTH / 2
        const itemCenter = rect.left + rect.width / 2
        const minCenter = VIEWPORT_MARGIN + halfPop
        const maxCenter = vw - VIEWPORT_MARGIN - halfPop
        // If the viewport is narrower than the popover, just center on the
        // viewport — overflow-x-auto on the popover handles the rest.
        const left = vw < POPOVER_APPROX_WIDTH + 2 * VIEWPORT_MARGIN
          ? vw / 2
          : Math.max(minCenter, Math.min(maxCenter, itemCenter))
        const fitsAbove = rect.top > POPOVER_APPROX_HEIGHT + VIEWPORT_MARGIN
        const top = fitsAbove
          ? rect.top - VIEWPORT_MARGIN
          : rect.bottom + VIEWPORT_MARGIN
        setPopoverPos({ top, left, below: !fitsAbove })
      }
      setPickerOpen(true)
    }, LONG_PRESS_MS)
  }
  const onPointerUp = () => cancelTimer()
  const onPointerCancel = () => cancelTimer()
  const onPointerLeave = () => cancelTimer()

  // If the long-press fired, swallow the synthetic click that follows so
  // we don't immediately close anything.
  const onClickCapture = (e: React.MouseEvent) => {
    if (longPressFired.current) {
      e.stopPropagation()
      e.preventDefault()
      longPressFired.current = false
    }
  }

  const toggle = (emoji: string) => {
    if (myEmojis.has(emoji)) {
      send('remove_reaction', { itemText, emoji })
    } else {
      send('add_reaction', { itemText, emoji })
    }
  }

  // Close the picker when the user taps anywhere outside.
  useEffect(() => {
    if (!pickerOpen) return
    const close = () => setPickerOpen(false)
    // Defer one frame so the opening touch doesn't immediately close it.
    const t = setTimeout(() => window.addEventListener('pointerdown', close, { once: true }), 0)
    return () => {
      clearTimeout(t)
      window.removeEventListener('pointerdown', close)
    }
  }, [pickerOpen])

  return (
    <div className="relative">
      <div
        ref={triggerRef}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={onPointerLeave}
        onClickCapture={onClickCapture}
        style={{ touchAction: 'manipulation', userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        {children}
      </div>

      {grouped.size > 0 && (
        // Half-overhanging badges anchored to the item's bottom-right corner.
        // translate-y-1/2 puts the badges' vertical center on the item's
        // bottom edge so half sits inside, half outside.
        <div className="absolute right-2 bottom-0 translate-y-1/2 z-10 flex gap-1 pointer-events-none">
          {Array.from(grouped.entries()).map(([emoji, users]) => (
            <button
              key={emoji}
              onClick={(e) => {
                e.stopPropagation()
                setNamesForEmoji(emoji)
              }}
              className="pointer-events-auto inline-flex items-center gap-0.5 rounded-full px-2 py-1 text-sm font-body bg-white border border-warmblack/15 shadow-md text-warmblack hover:bg-cream transition-colors"
            >
              <span className="leading-none">{emoji}</span>
              <span className="font-bold text-[11px] leading-none">{users.length}</span>
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {pickerOpen && popoverPos && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ type: 'spring', damping: 22, stiffness: 320 }}
            // position: fixed lets us anchor relative to the viewport so the
            // popover can never be clipped by an ancestor and can flip
            // above/below the trigger based on available room.
            // CSS `translate` (separate from `transform`) handles the
            // -50% horizontal centering without conflicting with framer's
            // scale transform.
            style={{
              position: 'fixed',
              top: popoverPos.top,
              left: popoverPos.left,
              translate: popoverPos.below ? '-50% 0' : '-50% -100%',
            }}
            className="z-50 max-w-[calc(100vw-1rem)] bg-white border border-warmblack/15 rounded-full shadow-xl px-2 py-1.5 flex gap-1 overflow-x-auto"
            onPointerDown={(e) => e.stopPropagation()}
          >
            {QUICK_REACTIONS.map((emoji) => {
              const mine = myEmojis.has(emoji)
              return (
                <button
                  key={emoji}
                  onClick={() => {
                    toggle(emoji)
                    setPickerOpen(false)
                  }}
                  className={`text-2xl rounded-full px-1.5 py-1 transition-colors ${
                    mine ? 'bg-coral/15' : 'hover:bg-warmblack/5'
                  }`}
                >
                  {emoji}
                </button>
              )
            })}
            <button
              onClick={() => {
                setPickerOpen(false)
                setFullPickerOpen(true)
              }}
              aria-label="More reactions"
              className="text-xl text-warmblack/60 hover:text-warmblack rounded-full w-9 h-9 flex items-center justify-center hover:bg-warmblack/5 transition-colors"
            >
              +
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {fullPickerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-warmblack/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3 pt-12"
            onClick={() => setFullPickerOpen(false)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-cream rounded-2xl shadow-2xl w-full max-w-sm p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="font-body text-sm text-warmblack/60">
                  React to <span className="font-bold text-warmblack">{itemText}</span>
                </p>
                <button
                  aria-label="Close"
                  onClick={() => setFullPickerOpen(false)}
                  className="w-7 h-7 rounded-lg hover:bg-warmblack/5 text-warmblack/60 text-xl flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-6 gap-1">
                {ALLOWED_REACTIONS.map((emoji) => {
                  const mine = myEmojis.has(emoji)
                  return (
                    <button
                      key={emoji}
                      onClick={() => {
                        toggle(emoji)
                        setFullPickerOpen(false)
                      }}
                      className={`text-2xl rounded-lg aspect-square flex items-center justify-center transition-colors ${
                        mine ? 'bg-coral/15 ring-2 ring-coral/40' : 'hover:bg-warmblack/5'
                      }`}
                    >
                      {emoji}
                    </button>
                  )
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {namesForEmoji && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-warmblack/40 backdrop-blur-sm flex items-center justify-center px-6"
            onClick={() => setNamesForEmoji(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-cream rounded-2xl shadow-2xl max-w-xs w-full p-5"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl">{namesForEmoji}</span>
                <span className="font-body text-warmblack/60 text-sm">on</span>
                <span className="font-body font-bold text-warmblack truncate">{itemText}</span>
              </div>
              <ul className="space-y-1">
                {(grouped.get(namesForEmoji) ?? []).map((uid) => (
                  <li key={uid} className="font-body text-base text-warmblack">
                    {room.players[uid]?.name || 'Left the room'}
                    {uid === playerId && <span className="text-warmblack/40 text-xs"> (you)</span>}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setNamesForEmoji(null)}
                className="mt-4 w-full bg-coral hover:bg-coral/90 text-white font-display font-bold py-2 rounded-xl transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
